import { FolderPlus } from 'lucide-react';
import { fmtMoney } from '../lib/format';
import type { DragSystem } from '../lib/dragSystem';
import type { CpiDefaults, ProjectedLine } from '../lib/projection';
import type { ProjectGroup } from '../lib/totals';
import type { LineItem, Project } from '../types';
import { ProjectBlock } from './ProjectBlock';
import { useIsViewer } from '../lib/viewerContext';

type Kind = 'income' | 'expense';

type Props = {
  groups: ProjectGroup[];
  totalNetCurrent: number;
  defaults: CpiDefaults;
  baseYear: number;
  projects: Project[];
  hidden: string[];
  showHidden: boolean;
  showArchivedIn: Set<string>;
  dragSystem: DragSystem;
  onToggleProjectArchived: (projectId: string) => void;
  startingLineNo: number;
  priorPeriod: string;
  actualByCode: Map<string, number>;
  quotaBoundLineCode?: string | null;
  onRenameProject: (id: string, name: string) => void;
  onMoveProject: (id: string, direction: 1 | -1) => void;
  onDeleteProject: (id: string) => void;
  onAddProject: () => void;
  onAddRow: (kind: Kind, projectId: string) => void;
  onLineUpdate: (kind: Kind) => (id: string, patch: Partial<LineItem>) => void;
  onLineDelete: (kind: Kind) => (id: string) => void;
  onLineToggleHidden: (id: string) => void;
  onLineAssignProject: (kind: Kind) => (id: string, projectId: string | null) => void;
  onLineRestore: (kind: Kind, id: string) => void;
};

export function ProjectsSection({
  groups,
  totalNetCurrent,
  defaults,
  baseYear,
  projects,
  hidden,
  showHidden,
  showArchivedIn,
  dragSystem,
  onToggleProjectArchived,
  startingLineNo,
  priorPeriod,
  actualByCode,
  quotaBoundLineCode,
  onRenameProject,
  onMoveProject,
  onDeleteProject,
  onAddProject,
  onAddRow,
  onLineUpdate,
  onLineDelete,
  onLineToggleHidden,
  onLineAssignProject,
  onLineRestore,
}: Props) {
  const isViewer = useIsViewer();
  let cursor = startingLineNo;
  const consume = (items: ProjectedLine[]) =>
    items.filter((i) => showHidden || !hidden.includes(i.id)).length;

  return (
    <section className="mb-12">
      <div className="flex items-baseline justify-between flex-wrap gap-2 pb-2 border-b border-edge">
        <h2 className="text-[18px] font-semibold text-ccaanz-gold tracking-[0.005em]">
          Discrete Activities
        </h2>
        <div className="text-xs text-ccaanz-grey">
          Projects with isolated income &amp; expenditure
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="mt-4 p-6 text-[13px] italic bg-paper-cream border border-dashed border-edge rounded-control text-ccaanz-grey">
          No projects defined. Click "+ New Project" below to add one.
        </div>
      ) : (
        <div className="mt-4">
          {groups.map((group, idx) => {
            const blockStart = cursor;
            cursor += consume(group.income) + consume(group.expenses);
            return (
              <ProjectBlock
                key={group.project.id}
                group={group}
                defaults={defaults}
                baseYear={baseYear}
                projects={projects}
                hidden={hidden}
                showHidden={showHidden}
                showArchived={showArchivedIn.has(group.project.id)}
                isFirst={idx === 0}
                isLast={idx === groups.length - 1}
                startingLineNo={blockStart}
                dragSystem={dragSystem}
                priorPeriod={priorPeriod}
                actualByCode={actualByCode}
                quotaBoundLineCode={quotaBoundLineCode ?? null}
                onRename={onRenameProject}
                onMove={onMoveProject}
                onDelete={onDeleteProject}
                onToggleArchived={onToggleProjectArchived}
                onAddRow={onAddRow}
                onLineUpdate={onLineUpdate}
                onLineDelete={onLineDelete}
                onLineToggleHidden={onLineToggleHidden}
                onLineAssignProject={onLineAssignProject}
                onLineRestore={onLineRestore}
              />
            );
          })}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
        {!isViewer ? (
          <button type="button" onClick={onAddProject} className="btn btn-ghost btn-sm no-print">
            <FolderPlus className="w-3.5 h-3.5" />
            New Project
          </button>
        ) : (
          <span />
        )}
        {groups.length > 0 && (
          <div className="text-[12px] text-ccaanz-grey">
            Total Discrete Activities Net (current year):{' '}
            <span
              className={`num font-semibold ${totalNetCurrent >= 0 ? 'text-ccaanz-blue' : 'text-ccaanz-black'}`}
            >
              {fmtMoney(totalNetCurrent)}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}
